<template>
  <section>
    <div
      class="relative w-full overflow-hidden bg-cover bg-center bg-no-repeat after:absolute after:left-0 after:top-0 after:block after:h-full after:w-full after:bg-gradient-to-r after:from-white after:to-white/0 after:content-['']"
      :style="bannerStyle"
    >
      <div class="container-wrap relative z-10 h-form-banner py-2">
        <div class="flex h-full w-full flex-col justify-center">
          <h1 class="text-h1 mb-3 line-clamp-2 tablet:mb-6">
            {{ t('title', { solutionName: solutionData?.banner?.title }) }}
          </h1>
          <div class="flex justify-between">
            <div class="mr-6">
              <p class="text-sm tablet:text-base">
                {{ t('intro', { solutionName: solutionData?.banner?.title }) }}
              </p>
              <ul class="my-6 ml-6 list-disc text-sm tablet:text-base">
                <li>{{ t('list-1') }}</li>
                <li>{{ t('list-2') }}</li>
                <li>{{ t('list-3') }}</li>
              </ul>
              <p class="text-sm tablet:text-base">{{ t('closing') }}</p>
            </div>

            <div
              v-if="overviewData"
              class="hidden desktop:block desktop:w-full desktop:max-w-[368px]"
            >
              <LightboxVideo
                v-if="overviewData.wistiaCode"
                :video-data="video"
                class="aspect-video w-full rounded"
              >
                <template #hidden>
                  <h3>{{ overviewData.title }}</h3>
                  <p>{{ overviewData.description }}</p>
                </template>
              </LightboxVideo>
              <figure
                v-else
                class="relative block aspect-video w-full overflow-hidden rounded"
              >
                <img
                  :src="contextThumbnail"
                  :alt="overviewData.title"
                  loading="lazy"
                  class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
                />
              </figure>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="overviewData" class="px-3 py-6 desktop:hidden">
      <LightboxVideo
        v-if="overviewData.wistiaCode"
        :video-data="video"
        class="aspect-video w-full rounded"
      >
        <template #hidden>
          <h3>{{ overviewData.title }}</h3>
          <p>{{ overviewData.description }}</p>
        </template>
      </LightboxVideo>
      <figure
        v-else
        class="relative block aspect-video w-full overflow-hidden rounded"
      >
        <img
          :src="contextThumbnail"
          :alt="overviewData.title"
          loading="lazy"
          class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
        />
      </figure>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';

  const { t } = useI18n({ useScope: 'local' });
  const { blobDomain } = useWebsite();

  const bannerStyle = computed(
    (): string =>
      `background-image: url(${blobDomain.value}/wise-paas-marketplace/solutions/form_banner.jpg)`
  );

  const { provideResponsiveImgUrl } = useSolutionsData();

  const solutionsStore = useSolutionsStore();

  const { solutionData, overviewData } = storeToRefs(solutionsStore);

  const video = computed(
    (): IVideoData => ({
      title: overviewData.value?.title ?? '',
      description: overviewData.value?.description ?? '',
      url: overviewData.value?.wistiaCode || '',
      imageUrl: contextThumbnail.value,
    })
  );

  const contextThumbnail = computed((): string => {
    const thumbnail = overviewData.value?.thumbnail;
    if (!thumbnail) return '';

    return provideResponsiveImgUrl(thumbnail);
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Discover how {solutionName} works – Book a demo today",
    "intro": "Discover how {solutionName} can help your business optimize operations, improve efficiency, and drive smarter decisions. Get a personalized demo to explore how our solution can:",
    "list-1": "Simplify workflows and enhance productivity",
    "list-2": "Deliver real-time insights for better decision-making",
    "list-3": "Adapt to your industry-specific needs",
    "closing": "Fill out the form, and our experts will reach out to schedule your demo. Let's unlock new possibilities together!"
  },
  "en-jp": {
    "title": "Discover how {solutionName} works – Book a demo today",
    "intro": "Discover how {solutionName} can help your business optimize operations, improve efficiency, and drive smarter decisions. Get a personalized demo to explore how our solution can:",
    "list-1": "Simplify workflows and enhance productivity",
    "list-2": "Deliver real-time insights for better decision-making",
    "list-3": "Adapt to your industry-specific needs",
    "closing": "Fill out the form, and our experts will reach out to schedule your demo. Let's unlock new possibilities together!"
  },
  "en-tw": {
    "title": "Discover how {solutionName} works – Book a demo today",
    "intro": "Discover how {solutionName} can help your business optimize operations, improve efficiency, and drive smarter decisions. Get a personalized demo to explore how our solution can:",
    "list-1": "Simplify workflows and enhance productivity",
    "list-2": "Deliver real-time insights for better decision-making",
    "list-3": "Adapt to your industry-specific needs",
    "closing": "Fill out the form, and our experts will reach out to schedule your demo. Let's unlock new possibilities together!"
  },
  "en-kr": {
    "title": "Discover how {solutionName} works – Book a demo today",
    "intro": "Discover how {solutionName} can help your business optimize operations, improve efficiency, and drive smarter decisions. Get a personalized demo to explore how our solution can:",
    "list-1": "Simplify workflows and enhance productivity",
    "list-2": "Deliver real-time insights for better decision-making",
    "list-3": "Adapt to your industry-specific needs",
    "closing": "Fill out the form, and our experts will reach out to schedule your demo. Let's unlock new possibilities together!"
  },
  "en-americas": {
    "title": "Discover how {solutionName} works – Book a demo today",
    "intro": "Discover how {solutionName} can help your business optimize operations, improve efficiency, and drive smarter decisions. Get a personalized demo to explore how our solution can:",
    "list-1": "Simplify workflows and enhance productivity",
    "list-2": "Deliver real-time insights for better decision-making",
    "list-3": "Adapt to your industry-specific needs",
    "closing": "Fill out the form, and our experts will reach out to schedule your demo. Let's unlock new possibilities together!"
  },
  "en-asia-pacific": {
    "title": "Discover how {solutionName} works – Book a demo today",
    "intro": "Discover how {solutionName} can help your business optimize operations, improve efficiency, and drive smarter decisions. Get a personalized demo to explore how our solution can:",
    "list-1": "Simplify workflows and enhance productivity",
    "list-2": "Deliver real-time insights for better decision-making",
    "list-3": "Adapt to your industry-specific needs",
    "closing": "Fill out the form, and our experts will reach out to schedule your demo. Let's unlock new possibilities together!"
  },
  "en-emea": {
    "title": "Discover how {solutionName} works – Book a demo today",
    "intro": "Discover how {solutionName} can help your business optimize operations, improve efficiency, and drive smarter decisions. Get a personalized demo to explore how our solution can:",
    "list-1": "Simplify workflows and enhance productivity",
    "list-2": "Deliver real-time insights for better decision-making",
    "list-3": "Adapt to your industry-specific needs",
    "closing": "Fill out the form, and our experts will reach out to schedule your demo. Let's unlock new possibilities together!"
  },
  "ja-jp": {
    "title": "{solutionName} の活用方法を知る — 今すぐデモを予約",
    "intro": "{solutionName} が業務の最適化、効率向上、よりスマートな意思決定をどのように実現するかをご確認ください。お客様に合わせたデモで、以下のポイントを詳しくご紹介します:",
    "list-1": "業務フローの簡素化と生産性向上",
    "list-2": "リアルタイムのインサイトによる迅速な意思決定",
    "list-3": "業界特有のニーズへの柔軟な対応",
    "closing": "フォームにご入力いただければ、担当よりデモ日程のご案内を差し上げます。共に新たな可能性を切り拓きましょう。"
  },
  "zh-cn": {
    "title": "了解 {solutionName} 如何运作 — 立即预约演示",
    "intro": "深入探索 {solutionName} 如何帮助您的企业优化运营、提升效率,并实现更智能的决策。申请个性化演示,全面了解本方案如何:",
    "list-1": "简化流程,提升工作效率",
    "list-2": "提供实时洞察,助力智能决策",
    "list-3": "灵活适应不同行业的专属需求",
    "closing": "填写表单后,我们的专家将与您联系安排演示时间。携手开启全新业务可能!"
  },
  "zh-tw": {
    "title": "深入了解 {solutionName} — 馬上預約展示",
    "intro": "探索 {solutionName} 如何協助企業優化營運流程、提升效率、並做出更聰明的決策。立即預約專屬展示,了解我們的解決方案如何:",
    "list-1": "簡化工作流程、提升生產力",
    "list-2": "提供即時洞察,強化決策能力",
    "list-3": "彈性滿足各產業的特定需求",
    "closing": "填寫表單後,我們的專家將主動與您聯繫安排展示時程。讓我們一起開啟更多創新可能!"
  }
}
</i18n>
